import { WebGLBackend } from "@/core/backends/webgl-backend"
import { Adjustments, ColorProfileName, Curve, LookTable } from "@/core/types"
import { Texture, TextureOptions } from "@/core/backends/webgl/texture"
import { vec2, vec3, vec4 } from "gl-matrix"
import { adobeColor } from "@/core/data/adobe-color"
import { adobeMonochrome } from "@/core/data/adobe-monochrome"
import { getAdobeColorProfileCurve } from "@/core/tone-curve"
import { Photo } from "@/core/photo"
import { DCPProfile, interpolateHueSatMap } from "@/core/dcp/camera-profile"
import {
	ComputedCameraDistortionModel,
	ComputedCameraLensCorrectionData,
} from "./utils/lens/lens-profile"
import { RawInfoProfileGainTableMap } from "@/lib/raw/fastlib"
import { Texture3D } from "./backends/webgl/texture-3d"

export interface PhotoEmbeddedProfile {
	name: string
	baselineExposure?: number
	gainTableMap?: RawInfoProfileGainTableMap
}

export class ProfileManager {
	backend: WebGLBackend

	// Color Look Table
	colorLookTable: LookTable
	colorLookTableTexture: Texture
	colorLookTableDims: vec3 = [1, 1, 1]

	// Monochrome Look Table
	monochromeLookTable: LookTable
	monochromeLookTableTexture: Texture
	monochromeLookTableDims: vec3 = [1, 1, 1]

	// Color Tone Curve
	profileCurveTexture: Texture
	profileCurve: Curve | null = null

	// Camera Look Tables
	profileLookTable: LookTable | null = null
	profileLookTableTexture?: Texture
	profileLookTableDims: vec3 = [1, 1, 1]

	// Camera Hue Sat Map
	hueSatMap: LookTable | null = null
	hueSatMapTexture?: Texture
	hueSatMapDims: vec3 = [1, 1, 1]

	// Camera Tone Curve?

	lensImageCenter: vec2 = [0, 0]
	lensNormalizationScale: number = 0.0
	lensAutoscaleFactor: number = 1.0
	lensDistortionModel: ComputedCameraDistortionModel | null = null
	lensDistortionParams: vec4 | null = null

	lensVignetteParams: vec3 | null = null

	lensChromaticAberrationParams: number[] | null = null
	lensChromaticAberrationParamsTexture: Texture

	// Embedded profile
	gainTableMapTexture: Texture3D | null = null
	embeddedProfile: PhotoEmbeddedProfile | null = null

	constructor(backend: WebGLBackend) {
		this.backend = backend

		// Adobe Color
		this.colorLookTable = adobeColor
		this.colorLookTableTexture = backend.createTexture({
			label: "profileManager.colorLookTableTexture",
			format: "rgb32float",
			data: adobeColor.data,
			width: adobeColor.satDivisions,
			height: adobeColor.hueDivisions * adobeColor.valDivisions,
		})
		this.colorLookTableDims = [
			adobeColor.hueDivisions,
			adobeColor.satDivisions,
			adobeColor.valDivisions,
		]

		// Adobe Monochrome
		this.monochromeLookTable = adobeMonochrome
		this.monochromeLookTableTexture = backend.createTexture({
			label: "profileManager.monochromeLookTableTexture",
			format: "rgb32float",
			data: adobeMonochrome.data,
			width: adobeMonochrome.satDivisions,
			height: adobeMonochrome.hueDivisions * adobeColor.valDivisions,
		})
		this.monochromeLookTableDims = [
			adobeMonochrome.hueDivisions,
			adobeMonochrome.satDivisions,
			adobeMonochrome.valDivisions,
		]

		this.profileCurveTexture = backend.createTexture({
			label: "profileManager.profileCurveTexture",
			format: "r32float",
			width: 0,
			height: 1,
		})

		this.lensChromaticAberrationParamsTexture = backend.createTexture({
			label: "profileManager.lensChromaticAberrationParamsTexture",
			format: "r32float",
			width: 12,
			height: 1,
		})
	}

	getColorLookTable(profileName: ColorProfileName): {
		lookTableDims: vec3
		lookTableTexture?: Texture
	} {
		// RawStandard is the default when no table is returned
		if (
			profileName === ColorProfileName.Color &&
			this.colorLookTable &&
			this.colorLookTableTexture
		) {
			return {
				lookTableTexture: this.colorLookTableTexture,
				lookTableDims: this.colorLookTableDims,
			}
		} else if (
			profileName === ColorProfileName.Monochrome &&
			this.monochromeLookTable &&
			this.monochromeLookTableTexture
		) {
			return {
				lookTableTexture: this.monochromeLookTableTexture,
				lookTableDims: this.monochromeLookTableDims,
			}
		}

		return { lookTableDims: this.colorLookTableDims, lookTableTexture: undefined }
	}

	getProfileCurveTexture() {
		return this.profileCurveTexture
	}

	getProfileLookTableTexture() {
		if (this.profileLookTable && this.profileLookTableTexture) {
			return this.profileLookTableTexture
		}
	}

	getHueSatMapTexture() {
		if (this.hueSatMap && this.hueSatMapTexture) {
			return this.hueSatMapTexture
		}
	}

	getColorProfile(
		adjustments: Partial<Adjustments>,
		options: { isSourceMonochrome?: boolean } = {},
	) {
		let colorProfileType = adjustments.colorProfile ?? null

		if (colorProfileType === null) {
			if (this.embeddedProfile) {
				colorProfileType = ColorProfileName.Embedded
			} else if (options.isSourceMonochrome) {
				colorProfileType = ColorProfileName.Monochrome
			} else {
				colorProfileType = ColorProfileName.Color
			}
		}

		return colorProfileType
	}

	getColorProfileForPhoto(photo: Photo) {
		return this.getColorProfile(photo.adjustments, {
			isSourceMonochrome: photo.isSourceMonochrome,
		})
	}

	getUniforms(photo: Photo) {
		if (photo.type === "bitmap") {
			let colorProfile = this.getColorProfileForPhoto(photo)

			// For bitmap images, we transform all profiles to either standard or standard monochrome
			// (no additional LUTs, just monochrome adjustments)
			if (
				colorProfile === ColorProfileName.Standard ||
				colorProfile === ColorProfileName.Color
			) {
				colorProfile = ColorProfileName.Standard
			} else if (
				colorProfile === ColorProfileName.Monochrome ||
				colorProfile === ColorProfileName.StandardMonochrome
			) {
				colorProfile = ColorProfileName.StandardMonochrome
			}

			return {
				renderProfileLookTable: false,
				renderHueSatMap: false,

				colorProfileType: colorProfile,
				colorLookTableDims: [0, 0, 0],

				profileLookTableDims: [0, 0, 0],
				hueSatMapDims: [0, 0, 0],

				profileLookTable: 9,
				hueSatMap: 10,
				colorLookTable: 11,
				profileCurveTexture: 12,
			}
		}

		const colorProfile = this.getColorProfileForPhoto(photo)

		const { lookTableTexture, lookTableDims } = this.getColorLookTable(colorProfile)

		const profileCurveTexture = this.getProfileCurveTexture()
		const profileLookTableTexture = this.getProfileLookTableTexture()
		const hueSatMapTexture = this.getHueSatMapTexture()

		profileLookTableTexture?.bind(9)
		hueSatMapTexture?.bind(10)
		lookTableTexture?.bind(11)
		profileCurveTexture?.bind(12)

		return {
			renderProfileLookTable: !!profileLookTableTexture,
			renderHueSatMap: !!hueSatMapTexture,

			colorProfileType: colorProfile,
			colorLookTableDims: lookTableDims,

			profileLookTableDims: this.profileLookTableDims,
			hueSatMapDims: this.hueSatMapDims,

			profileLookTable: 9,
			hueSatMap: 10,
			colorLookTable: 11,
			profileCurveTexture: 12,
		}
	}

	getLensUniforms(photo: Photo) {
		return {
			/* Lensfun */
			renderLensDistortionCorrection:
				(photo.adjustments.enableLensCorrections ??
					(this.isLensVignetteCorrectable() || this.isLensDistortionCorrectable())) &&
				photo.type !== "bitmap" &&
				this.lensDistortionParams != null &&
				photo.adjustments.correctLensDistortion,

			lensDistortionCorrectionFactor: photo.adjustments.lensDistortionCorrectionFactor,

			lensImageSize: photo.imageState.originalImageSize,
			lensImageCenter: this.lensImageCenter,
			lensNormalizationScale: this.lensNormalizationScale,
			lensAutoscaleFactor: this.lensAutoscaleFactor,

			lensDistortionParams: (this.lensDistortionParams?.slice(0, 4) ?? [0, 0, 0, 0]) as vec4,
			lensDistortion: photo.adjustments.lensDistortion,

			lensACMDistortionModel: this.lensDistortionModel == "acm",
		}
	}

	getVignetteUniforms(photo: Photo) {
		return {
			renderLensVignetteCorrection:
				(photo.adjustments.enableLensCorrections ??
					(this.isLensVignetteCorrectable() || this.isLensDistortionCorrectable())) &&
				this.lensVignetteParams != null &&
				photo.adjustments.correctLensVignette,
			lensVignetteParams: this.lensVignetteParams?.slice(0, 3) ?? [0, 0, 0],
			lensVignetteCorrectionFactor: photo.adjustments.lensVignetteCorrectionFactor,
		}
	}

	getChromaticAberrationUniforms(photo: Photo) {
		this.lensChromaticAberrationParamsTexture.bind(13)

		return {
			renderChromaticAberrationCorrection:
				this.lensChromaticAberrationParams !== null &&
				photo.adjustments.correctLensChromaticAberration,
			lensChromaticAberrationParams: 13,
		}
	}

	isLensDistortionCorrectable() {
		return this.lensDistortionParams !== null
	}

	isLensVignetteCorrectable() {
		return this.lensVignetteParams !== null
	}

	isLensChromaticAberrationCorrectable() {
		return this.lensChromaticAberrationParams !== null
	}

	setLensProfile(profile?: ComputedCameraLensCorrectionData) {
		this.lensImageCenter = [0.5, 0.5]
		this.lensNormalizationScale = 0.0
		this.lensAutoscaleFactor = 1.0
		this.lensDistortionModel = null
		this.lensDistortionParams = null
		this.lensVignetteParams = null
		this.lensChromaticAberrationParams = null

		if (!profile) {
			return
		}

		this.lensNormalizationScale = profile.normalizationScale
		this.lensAutoscaleFactor = profile.autoscaleFactor
		this.lensImageCenter = profile.imageCenter
		this.lensDistortionModel = profile.distortionModel
		this.lensDistortionParams = profile.distortionParam

		this.lensVignetteParams = profile.vignetteParam

		this.lensChromaticAberrationParams = profile.chromaticAberrationParam

		if (this.lensChromaticAberrationParams) {
			this.lensChromaticAberrationParamsTexture.update({
				data: new Float32Array(this.lensChromaticAberrationParams),
			})
		}
	}

	setProfile(photo: Photo, profile?: DCPProfile | null, profileToneCurve?: Float32Array) {
		this.profileLookTable = null
		this.hueSatMap = null
		this.profileCurve = null

		if (profileToneCurve && profileToneCurve.length > 0) {
			// This profileCurve is only used for AI color match
			if (profileToneCurve.length > 1) {
				this.profileCurve = Array.from(profileToneCurve).map((y, i) => [
					(i / (profileToneCurve.length - 1)) * 255,
					y * 255,
				])
			}

			this.profileCurveTexture.update({
				data: profileToneCurve,
				width: profileToneCurve.length,
				height: 1,
			})
		} else {
			const data = getAdobeColorProfileCurve()

			this.profileCurveTexture.update({
				data,
				width: data.length,
				height: 1,
			})
		}

		if (!profile) {
			return
		}

		const lookTable = profile.lookTable

		if (lookTable) {
			const options: TextureOptions = {
				label: "profileManager.lookTableTexture",
				format: "rgb32float",
				data: lookTable.data,
				width: lookTable.satDivisions,
				height: lookTable.hueDivisions * lookTable.valDivisions,
			}

			if (this.profileLookTableTexture) {
				this.profileLookTableTexture.update(options)
			} else {
				this.profileLookTableTexture = this.backend.createTexture(options)
			}

			this.profileLookTable = lookTable

			this.profileLookTableDims = [
				lookTable.hueDivisions,
				lookTable.satDivisions,
				lookTable.valDivisions,
			]
		}

		const hueSatMap = interpolateHueSatMap(profile, photo.whiteBalance.asShotTemperature)

		if (hueSatMap) {
			const options: TextureOptions = {
				label: "profileManager.hueSatMapTexture",
				format: "rgb32float",
				data: hueSatMap.data,
				width: hueSatMap.satDivisions,
				height: hueSatMap.hueDivisions * hueSatMap.valDivisions,
			}

			if (this.hueSatMapTexture) {
				this.hueSatMapTexture.update(options)
			} else {
				this.hueSatMapTexture = this.backend.createTexture(options)
			}

			this.hueSatMap = hueSatMap

			this.hueSatMapDims = [
				hueSatMap.hueDivisions,
				hueSatMap.satDivisions,
				hueSatMap.valDivisions,
			]
		}
	}

	setEmbeddedProfile(embeddedProfile: PhotoEmbeddedProfile | null) {
		if (!embeddedProfile) {
			this.embeddedProfile = null
			this.gainTableMapTexture?.delete()
			this.gainTableMapTexture = null
			return
		}

		this.embeddedProfile = embeddedProfile

		const gainTableMap = embeddedProfile.gainTableMap

		if (gainTableMap) {
			this.gainTableMapTexture = this.backend.createTexture3D({
				label: "profileManager.gainTableMapTexture",
				format: "r32float",
				width: gainTableMap.points_h,
				height: gainTableMap.points_v,
				depth: gainTableMap.points_n,
				data: gainTableMap.data,
			})
		}
	}

	// These following 3 functions are used for AI color match parameter estimation
	getProfileCurve(photo: Photo): Curve | null {
		if (photo.type === "bitmap") {
			return null
		}

		const colorProfile = this.getColorProfileForPhoto(photo)

		if (
			colorProfile === ColorProfileName.Color ||
			colorProfile === ColorProfileName.Monochrome
		) {
			if (this.profileCurve) {
				return this.profileCurve
			}
			// Adobe Color
			return [
				[0, 0],
				[22, 16],
				[40, 35],
				[127, 127],
				[224, 230],
				[240, 246],
				[255, 255],
			]
		}

		return null
	}

	getAppliedColorLookTable(photo: Photo) {
		if (photo.type === "bitmap") {
			return null
		}

		const profileName = this.getColorProfileForPhoto(photo)

		if (
			profileName === ColorProfileName.Color &&
			this.colorLookTable &&
			this.colorLookTableTexture
		) {
			return this.colorLookTable
		} else if (
			profileName === ColorProfileName.Monochrome &&
			this.monochromeLookTable &&
			this.monochromeLookTableTexture
		) {
			return this.monochromeLookTable
		}

		return null
	}

	getAppliedProfileLookTable(photo: Photo) {
		if (photo.adjustments.disableProfileLookTable) {
			return null
		}

		if (this.profileLookTable && this.profileLookTableTexture) {
			return this.profileLookTable
		}
		return null
	}

	reset() {
		this.setEmbeddedProfile(null)
		this.setLensProfile(undefined)
	}
}
